import { useState, useRef, useCallback } from 'react';
import { Upload, CheckCircle, Clock, XCircle, FileText, AlertCircle, Calendar, Star, ImageIcon, Info } from 'lucide-react';
import { useAppAuth } from '../hooks/useAppAuth';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createApiClient, documentsService, profileService } from '../services/api';
import { useRefresh } from '../hooks/useRefresh';
import { RefreshButton } from '../components/RefreshButton';
import { DocumentType } from '../types';
import type { DocumentUpload } from '../types';

type Role = 'Carrier' | 'Workshop';

interface DocMeta {
  type: DocumentType;
  label: string;
  hint: string;
  roles: Role[];
  acceptsPdf: boolean;
}

const DOCUMENTS: DocMeta[] = [
  {
    type: DocumentType.GoodsInTransitInsurance,
    label: 'Goods in Transit (GIT) Insurance',
    hint: 'Upload your GIT insurance certificate showing coverage for goods you transport.',
    roles: ['Carrier', 'Workshop'],
    acceptsPdf: true,
  },
  {
    type: DocumentType.PublicLiabilityInsurance,
    label: 'Public Liability Insurance',
    hint: 'Upload your public liability insurance certificate (minimum £1M coverage recommended).',
    roles: ['Carrier', 'Workshop'],
    acceptsPdf: true,
  },
];

function StatusBadge({ status }: { status: string }) {
  if (status === 'Approved') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 shrink-0">
      <CheckCircle className="w-3 h-3" />Approved
    </span>
  );
  if (status === 'Rejected') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 shrink-0">
      <XCircle className="w-3 h-3" />Rejected
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 shrink-0">
      <Clock className="w-3 h-3" />Pending Review
    </span>
  );
}

function DocumentCard({ meta, uploads, getToken, userRole, onUploadDone }: {
  meta: DocMeta;
  uploads: DocumentUpload[];
  getToken: () => Promise<string>;
  userRole: Role;
  onUploadDone: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [expiryDate, setExpiryDate] = useState('');
  const [expiryError, setExpiryError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const today = new Date().toISOString().split('T')[0];

  const handleExpiryChange = (value: string) => {
    setExpiryDate(value);
    setExpiryError(null);
  };
  const [success, setSuccess] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sorted = [...uploads].sort(
    (a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()
  );
  const latest = sorted[0];
  const history = sorted.slice(1);

  const acceptAttr = meta.acceptsPdf
    ? 'image/png,image/jpeg,image/webp,application/pdf'
    : 'image/png,image/jpeg,image/webp';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) { setError('Please select a file.'); return; }
    if (expiryError) return;
    setUploading(true);
    setError(null);
    setSuccess(false);
    try {
      const svc = documentsService(createApiClient(getToken));
      await svc.upload(file, meta.type, userRole, expiryDate || undefined);
      setSuccess(true);
      setFile(null);
      setExpiryDate('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      onUploadDone();
    } catch (err: any) {
      setError(err?.response?.data?.title ?? err?.message ?? 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      {/* Card header */}
      <div className="px-5 py-4 flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 text-sm">{meta.label}</h3>
          <p className="text-xs text-gray-500 mt-0.5">{meta.hint}</p>
        </div>
        {latest ? <StatusBadge status={latest.reviewStatus} /> : (
          <span className="text-xs text-gray-400 shrink-0">Not submitted</span>
        )}
      </div>

      {/* Latest upload info */}
      {latest && (
        <div className={`px-5 py-2.5 border-t border-b text-xs flex flex-col gap-1 ${
          latest.reviewStatus === 'Rejected' ? 'bg-red-50 border-red-100' : 'bg-gray-50 border-gray-100'
        }`}>
          <div className="flex items-center gap-2 text-gray-600">
            <FileText className="w-3.5 h-3.5 shrink-0 text-gray-400" />
            <span className="truncate font-medium">{latest.originalFileName}</span>
            <span className="text-gray-400 shrink-0">
              Â· {new Date(latest.uploadedAt).toLocaleDateString('en-GB')}
            </span>
            {latest.expiryDate && (
              <span className="text-gray-400 shrink-0">
                Â· Expires {new Date(latest.expiryDate).toLocaleDateString('en-GB')}
              </span>
            )}
          </div>
          {latest.reviewStatus === 'Rejected' && latest.reviewNotes && (
            <p className="text-red-600 pl-5">
              <span className="font-medium">Rejected: </span>{latest.reviewNotes}
            </p>
          )}
          {history.length > 0 && (
            <button
              type="button"
              onClick={() => setShowHistory(h => !h)}
              className="text-blue-500 hover:text-blue-700 pl-5 text-left"
            >
              {showHistory ? 'Hide' : 'Show'} {history.length} previous version{history.length > 1 ? 's' : ''}
            </button>
          )}
          {showHistory && history.map(u => (
            <div key={u.documentId} className="pl-5 flex items-center gap-2 text-gray-400">
              <FileText className="w-3 h-3 shrink-0" />
              <span className="truncate">{u.originalFileName}</span>
              <span className="shrink-0">Â· {new Date(u.uploadedAt).toLocaleDateString('en-GB')}</span>
              <StatusBadge status={u.reviewStatus} />
            </div>
          ))}
        </div>
      )}

      {/* Upload form */}
      <form onSubmit={handleSubmit} className="px-5 py-4 space-y-3">
        {latest && !success && (
          <p className="text-xs text-gray-500 font-medium">
            {latest.reviewStatus === 'Rejected' ? 'Upload a corrected document:' : 'Upload a replacement:'}
          </p>
        )}

        <div className="flex gap-2 items-center flex-wrap sm:flex-nowrap">
          {/* File picker */}
          <div
            className="flex-1 min-w-0 border border-dashed border-gray-300 rounded-lg px-3 py-2 text-xs cursor-pointer hover:border-blue-400 transition-colors flex items-center gap-2"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            {file ? (
              <span className="text-gray-700 font-medium truncate">{file.name}</span>
            ) : (
              <span className="text-gray-400">
                {latest ? 'Choose replacement file' : 'Choose file to upload'}
              </span>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptAttr}
            className="hidden"
            onChange={e => { setFile(e.target.files?.[0] ?? null); setError(null); setSuccess(false); }}
          />

          {/* Expiry date */}
          <div className="shrink-0 w-36">
            <label className="block text-xs text-gray-500 mb-1">Expiry Date</label>
            <div className="relative">
            <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
            <input
              type="date"
              value={expiryDate}
              min={today}
              onChange={e => handleExpiryChange(e.target.value)}
              title="Document expiry date (optional)"
              className={`w-full border rounded-lg pl-6 pr-2 py-2 text-xs focus:outline-none focus:ring-2 ${
                expiryError
                  ? 'border-red-400 focus:ring-red-400'
                  : 'border-gray-300 focus:ring-blue-400'
              }`}
            />
            </div>
            {expiryError && (
              <p className="text-xs text-red-600 mt-1">{expiryError}</p>
            )}
          </div>

          {/* Upload button */}
          <button
            type="submit"
            disabled={uploading || !file || !!expiryError}
            className="shrink-0 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors flex items-center gap-1.5"
          >
            {uploading
              ? <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : <Upload className="w-3 h-3" />
            }
            {uploading ? 'Uploading' : 'Save'}
          </button>
        </div>

        {error && (
          <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{error}
          </div>
        )}
        {success && (
          <div className="flex items-start gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
            <CheckCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />Submitted for verification.
          </div>
        )}
      </form>
    </div>
  );
}

//  External Ratings Setup 

interface ScreenshotUpload {
  uploadId: string;
  platform: string;
  userRole: string;
  originalFileName: string;
  status: 'Pending' | 'Approved' | 'Rejected';
  approvedRating: number | null;
  approvedReferenceText: string | null;
  rejectionReason: string | null;
  uploadedAt: string;
  reviewedAt: string | null;
  screenshotSasUrl: string | null;
  aiExtractedRating: number | null;
}

const RATING_PLATFORMS = ['Checkatrade', 'Trustpilot', 'Google', 'Other'] as const;
type RatingPlatform = (typeof RATING_PLATFORMS)[number];

const PLATFORM_HINTS: Record<RatingPlatform, string> = {
  Checkatrade: 'Screenshot your Checkatrade profile showing your star rating and review count.',
  Trustpilot:  'Screenshot your Trustpilot business page showing your TrustScore and review count.',
  Google:      'Screenshot your Google Business profile showing your star rating and review count.',
  Other:       'Screenshot any verifiable external ratings page showing your business name and rating.',
};

function StarRating({ rating }: { rating: number }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} className={`w-3.5 h-3.5 ${i <= Math.round(rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`} />
      ))}
      <span className="ml-1 text-sm font-semibold text-gray-700">{(rating ?? 0).toFixed(1)}</span>
    </span>
  );
}

function ExternalRatingsSetupSection({ getToken, userRole }: {
  getToken: () => Promise<string>;
  userRole: Role;
}) {
  const [platform, setPlatform] = useState<RatingPlatform>('Checkatrade');
  const [file, setFile]         = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [success, setSuccess]   = useState(false);
  const [aiRating, setAiRating] = useState<number | null>(null);
  const fileInputRef            = useRef<HTMLInputElement>(null);

  const { data: uploads = [], isLoading, refetch } = useQuery<ScreenshotUpload[]>({
    queryKey: ['my-rating-screenshots'],
    queryFn: async () => {
      const r = await createApiClient(getToken).get<ScreenshotUpload[]>('/api/v1/ratings/screenshots/my');
      return r.data;
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) { setError('Please select a screenshot.'); return; }
    setUploading(true);
    setError(null);
    setSuccess(false);
    try {
      const form = new FormData();
      form.append('screenshot', file);
      form.append('platform', platform);
      form.append('userRole', userRole);
      const res = await createApiClient(getToken).post<ScreenshotUpload>(
        '/api/v1/ratings/screenshots', form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setAiRating(res.data?.aiExtractedRating ?? null);
      setSuccess(true);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      refetch();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { title?: string } }; message?: string };
      setError(e?.response?.data?.title ?? e?.message ?? 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-5 py-4 flex items-start justify-between gap-3 border-b border-gray-100">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 text-sm">External Rating Verification</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Upload a screenshot of your Checkatrade, Trustpilot, or Google rating.
            Support will verify it and display your approved rating on your public profile.
            {uploads.length === 0 && !isLoading && (
              <> This is a one-time setup  complete it now to build instant credibility with customers.</>
            )}
          </p>
        </div>
      </div>

      {/* Existing submissions */}
      {isLoading ? (
        <div className="flex justify-center py-6">
          <div className="animate-spin w-5 h-5 border-4 border-blue-500 border-t-transparent rounded-full" />
        </div>
      ) : uploads.length > 0 && (
        <div className="px-5 py-3 border-b border-gray-100 space-y-3">
          {uploads.map(u => (
            <div key={u.uploadId} className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-gray-800">{u.platform}</span>
                  <span className="text-xs text-gray-400">{new Date(u.uploadedAt).toLocaleDateString('en-GB')}</span>
                  {u.screenshotSasUrl && (
                    <a
                      href={u.screenshotSasUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-blue-500 hover:text-blue-700 underline"
                    >
                      View screenshot
                    </a>
                  )}
                </div>
                {u.status === 'Approved' && u.approvedRating != null && (
                  <div className="mt-1"><StarRating rating={u.approvedRating} /></div>
                )}
                {u.status === 'Approved' && u.approvedReferenceText && (
                  <p className="text-xs text-gray-500 italic mt-0.5">"{u.approvedReferenceText}"</p>
                )}
                {u.status === 'Rejected' && u.rejectionReason && (
                  <p className="text-xs text-red-600 mt-0.5">Rejected: {u.rejectionReason}  please re-upload a clearer screenshot.</p>
                )}
              </div>
              <StatusBadge status={u.status} />
            </div>
          ))}
        </div>
      )}

      {/* Upload form */}
      <form onSubmit={handleSubmit} className="px-5 py-4 space-y-3">
        {uploads.length > 0 && (
          <p className="text-xs text-gray-500 font-medium">Add another platform:</p>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {RATING_PLATFORMS.map(p => (
            <button
              key={p}
              type="button"
              onClick={() => setPlatform(p)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                platform === p
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
              }`}
            >
              {p}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-400">{PLATFORM_HINTS[platform]}</p>

        <div className="flex gap-2 items-center flex-wrap sm:flex-nowrap">
          <div
            className="flex-1 min-w-0 border border-dashed border-gray-300 rounded-lg px-3 py-2 text-xs cursor-pointer hover:border-blue-400 transition-colors flex items-center gap-2"
            onClick={() => fileInputRef.current?.click()}
          >
            <ImageIcon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            {file ? (
              <span className="text-gray-700 font-medium truncate">{file.name}</span>
            ) : (
              <span className="text-gray-400">Choose screenshot (PNG / JPEG, max 5 MB)</span>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={e => { setFile(e.target.files?.[0] ?? null); setError(null); setSuccess(false); }}
          />
          <button
            type="submit"
            disabled={uploading || !file}
            className="shrink-0 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors flex items-center gap-1.5"
          >
            {uploading
              ? <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : <Upload className="w-3 h-3" />
            }
            {uploading ? 'Uploading' : 'Submit'}
          </button>
        </div>

        {error && (
          <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{error}
          </div>
        )}
        {success && (
          <div className="flex flex-col gap-1 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
            <div className="flex items-start gap-2">
              <CheckCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              Submitted for review. Our support team will verify it within 24 hours.
            </div>
            {aiRating != null && (
              <div className="flex items-center gap-1.5 pl-5">
                <Info className="w-3 h-3 text-green-600 shrink-0" />
                <span className="text-green-600">
                  AI detected a rating of <strong>{aiRating.toFixed(1)}</strong> from your screenshot — your image is clear.
                </span>
              </div>
            )}
          </div>
        )}
      </form>
    </div>
  );
}

export default function DocumentsPage() {
  const { getToken, account } = useAppAuth();
  const queryClient = useQueryClient();

  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: () => profileService(createApiClient(getToken)).getProfile(),
    enabled: !!account,
  });

  const profileRole = profile?.role ?? '';
  const userRole: Role = profileRole === 'Workshop' ? 'Workshop' : 'Carrier';
  const isCustomer = profileRole === 'Customer';

  const {
    data: uploads = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['my-documents'],
    queryFn: () => documentsService(createApiClient(getToken)).getMy(),
    enabled: !!account && !isCustomer,
  });

  const { isRefreshing, lastRefreshed, refresh } = useRefresh(
    useCallback(() => { refetch(); }, [refetch])
  );

  if (isCustomer) return (
    <div className="page-shell text-center">
      <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
      <h2 className="text-lg font-semibold text-gray-700 mb-2">Not applicable to your account</h2>
      <p className="text-sm text-gray-500">
        Compliance documents are required only for Drivers and Workshops.
      </p>
    </div>
  );

  const visibleDocs = DOCUMENTS.filter(d => d.roles.includes(userRole));

  const uploadsForType = (type: DocumentType) =>
    uploads.filter(u => u.documentType === type);

  return (
    <div className="page-shell space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Business Insurance</h1>
          <p className="mt-1 text-sm text-gray-500">
            Upload your business-level insurance certificates for admin review. MOT and vehicle
            insurance are managed per-vehicle on the Vehicles page. Your driving licence is on the
            Drivers page.
          </p>
        </div>
        <RefreshButton onRefresh={refresh} isRefreshing={isRefreshing} lastRefreshed={lastRefreshed} />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin w-6 h-6 border-4 border-blue-500 border-t-transparent rounded-full" />
        </div>
      ) : (
        <div className="space-y-4">
          {visibleDocs.map(meta => (
            <DocumentCard
              key={meta.type}
              meta={meta}
              uploads={uploadsForType(meta.type)}
              getToken={getToken}
              userRole={userRole}
              onUploadDone={() => refetch()}
            />
          ))}
        </div>
      )}

      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">External Ratings</h2>
        <ExternalRatingsSetupSection getToken={getToken} userRole={userRole} />
      </div>
    </div>
  );
}

